import React, { useState, useEffect } from 'react';
import './style.css';
import { Button, message, notification } from 'antd';
import { ArrowLeftOutlined, InfoCircleFilled } from '@ant-design/icons';
import { useBlocker, useNavigate } from 'react-router-dom';
import { useIsFormChanged } from '../../../../store/devices/getDeviceChange';
import { Device } from '../../../../types/Device';
import { useAuthStore } from '../../../../store/auth/auth';
import { PhotoRecord } from '../../../../api/fotoRec/PhotoRec';
import { useFilesStore } from '../../../../store/devices/FilesDevicesFromDB';
import RecordingBar from './components/RecordingBar';
import { useSelectedLayout } from '../../../../store/useSelectedLayout';
import useRecordingStore from '../../../../pages/pagesV2/layouts/pages/layout/api/recording/recordingStore';
import useNotification from '../../../../utils/features/hooks/useNotification';
import ButtonRecordVideo from '../../../../pages/pagesV2/layouts/pages/layout/components/buttons/buttonForToolBarDeviceOne/ButtonRecordVideo';
import ButtonTakeAPhoto from '../../../../pages/pagesV2/layouts/pages/layout/components/buttons/buttonForToolBarDeviceOne/ButtonTakeAPhoto';
import ButtonRecordAudio from '../../../../pages/pagesV2/layouts/pages/layout/components/buttons/buttonForToolBarDeviceOne/ButtonRecordAudio';
import { useSelectedDevice } from '../../../../store/devices/SelectedDevice';

interface ToolBarDeviceOneProps {
    device: Device | null;
    onRecordingStateChange: (type: 'video' | 'audio' | null) => void;
    refreshFiles: () => void;
}

const ToolBarDeviceOne: React.FC<ToolBarDeviceOneProps> = ({
    device,
    onRecordingStateChange,
    refreshFiles,
}) => {
    const navigate = useNavigate();
    const [api, contextHolder] = notification.useNotification();
    const { user, SmartDVRToken } = useAuthStore();
    const [notificationKey, setNotificationKey] = useState<string | null>(null);
    const { fetchFiles } = useFilesStore();
    const { selectedDevice, setSelectedDevice } = useSelectedDevice();

    const { startRecording, stopRecording, getDeviceRecordingType, isDeviceRecording } =
        useRecordingStore();
    const { showNotification, contextHolderr } = useNotification();

    const isRecording = device ? isDeviceRecording(device) : null;
    const recordingType = device ? getDeviceRecordingType(device) : null;
    console.log(device);
    // Обработчик начала записи
    const handleStartRecording = async (type: 'video' | 'audio') => {
        if (!device || !device.online || !SmartDVRToken || !user?.login) {
            openNotificationNotOnline();
            return;
        }

        if (!device.online) return false;

        const isRecording = isDeviceRecording(device);
        const recordingType = getDeviceRecordingType(device);

        if (type === 'video') {
            if (isRecording && recordingType === 'audio') {
                // Особый случай: можно начать видео-запись поверх аудио
            } else if (isRecording) {
                // Уже идет запись (не аудио) - нельзя начать новую
                showNotification('error', 'Ошибка', 'Устройство уже записывает видео');
                return;
            }
        } else if (type === 'audio') {
            if (isRecording) {
                // Уже идет запись - нельзя начать аудио
                showNotification('error', 'Ошибка', 'Устройство уже записывает');
                return;
            }
        }
        // Для фото доступны все онлайн устройства

        // Если уже идет запись, сначала останавливаем текущую
        // if (recordingType) {
        //     await stopRecording(recordingType, [device]);
        // }

        try {
            // Начинаем новую запись
            await startRecording(type, [device]);
            setSelectedDevice({
                ...device,
                currentStatus: type === 'audio' ? 'пишет аудио' : 'пишет видео',
            });
            showNotification(
                'warning',
                'Запись файла',
                `Ведется запись ${type === 'audio' ? 'аудио' : 'видео'} на "${device.name}"`,
            );
        } catch (error) {
            console.error(`Error starting ${type} recording:`, error);
            notification.error({
                message: 'Ошибка',
                description: `Не удалось начать запись ${type === 'video' ? 'видео' : 'аудио'}`,
            });
        }
    };

    // Обработчик остановки записи
    const handleStopRecording = async (type: 'video' | 'audio' | 'photo') => {
        if (!device || !SmartDVRToken || !user?.login) return;

        if (!device.online) {
            message.error(`Нельзя остановить запись: устройство (${device.name}) не онлайн`);
            return;
        }

        try {
            await stopRecording(type, [device]);
            setSelectedDevice({
                ...device,
                currentStatus:
                    type === 'audio' ? 'остановка аудио записи' : 'остановка видео записи',
            });
            showNotification(
                'success',
                'Файлы записаны',
                `Сохранены ${type === 'audio' ? 'аудио' : 'видео'}`,
            );

            setTimeout(refreshFiles, 5000);
        } catch (error) {
            console.error(`Error stopping ${type} recording:`, error);
        }
    };

    // Обработчик фото
    const handleTakeAPhoto = async () => {
        if (!device || !device.online || !SmartDVRToken || !user?.login) {
            openNotificationNotOnline();
            return;
        }

        try {
            await PhotoRecord(SmartDVRToken, user.login, { UID: device.UID });
            openNotificationEnd('photo');

            // // Обновляем список файлов после фото
            // setTimeout(() => {
            //     refreshFiles();
            // }, 10000);
        } catch (error) {
            console.error('Error taking photo:', error);
            notification.error({
                message: 'Ошибка',
                description: 'Не удалось сделать фото',
            });
        }
    };

    const canStartVideo = (device: Device | null) => {
        if (!device?.online) {
            return false; // Устройство должно быть онлайн
        }

        const isRecording = isDeviceRecording(device);
        const recordingType = getDeviceRecordingType(device);

        // Можно начать видео:
        // 1. Если нет записи
        // 2. Если идет запись аудио (можно поверх)
        return !isRecording || (isRecording && recordingType === 'audio');
    };

    const canStartAudio = (device: Device | null) => {
        if (!device?.online) {
            return false; // Устройство должно быть онлайн
        }

        const isRecording = isDeviceRecording(device);

        // Можно начать аудио только если нет текущей записи
        return !isRecording;
    };

    const canTakePhoto = (device: Device | null) => {
        return device?.online ?? false;
    };

    // Уведомления
    const openNotificationStart = (type: 'video' | 'audio') => {
        const key = `recording-${Date.now()}`;
        setNotificationKey(key);

        api.open({
            key,
            message: 'Идет запись',
            description: `Запись ${type === 'video' ? 'видео' : 'аудио'} на устройстве "${device?.name}"`,
            duration: 0,
            icon: <InfoCircleFilled style={{ color: '#FDB159' }} />,
        });
    };

    const openNotificationEnd = (type: 'video' | 'audio' | 'photo') => {
        api.success({
            message: 'Запись завершена',
            description: `Файл ${type === 'video' ? 'видео' : type === 'audio' ? 'аудио' : 'фото'} сохранен`,
            duration: 3,
        });
    };

    const closeNotification = () => {
        if (notificationKey) {
            api.destroy(notificationKey);
            setNotificationKey(null);
        }
    };

    const openNotificationNotOnline = () => {
        api.warning({
            message: 'Устройство не в сети',
            description: 'Невозможно выполнить запись, так как устройство не в сети',
            duration: 3,
        });
    };

    return (
        <>
            <div className="toolBarDeviceOne">
                {contextHolder}
                <div className="leftSideToolBar">
                    <Button
                        className="buttonLeft"
                        icon={<ArrowLeftOutlined />}
                        style={{ border: 'none' }}
                        onClick={() => navigate(-1)}
                    >
                        {device?.name}
                    </Button>
                </div>
                <div className="rightSideToolBar">
                    <ButtonRecordVideo
                        onClick={() => handleStartRecording('video')}
                        disabled={!canStartVideo(device)}
                    />
                    <ButtonTakeAPhoto onClick={handleTakeAPhoto} disabled={!canTakePhoto(device)} />
                    <ButtonRecordAudio
                        onClick={() => handleStartRecording('audio')}
                        disabled={!canStartAudio(device)}
                    />
                </div>

                {isRecording && recordingType && device && (
                    <RecordingBar
                        device={device}
                        recordingType={recordingType}
                        onStop={() => {
                            handleStopRecording(recordingType);
                        }}
                    />
                )}
            </div>
        </>
    );
};

export default ToolBarDeviceOne;
